import {useI18n} from 'i18n/I18nContext'
import React, {useMemo} from 'react'
import {useEditor} from 'TemplateEditor/EditorProvider'
import {PaddingInput} from 'TemplateEditor/Layout/Blocks/ConfigInputs/PaddingInput'
import {
  Padding,
  StyledTitle,
} from 'TemplateEditor/Layout/Blocks/DefaultBlockConfig'
import {
  getDefaultPaddingStorage,
  zeroPadding,
} from 'TemplateEditor/defaultPaddingUtils'
import type {
  Template,
  TemplateDefaultPaddingStorage,
} from 'TemplateEditor/template'
import {useTemplate} from 'TemplateEditor/TemplateProvider'

export function TemplateGlobalPaddingPanel() {
  const {t} = useI18n()
  const {editMode} = useEditor()
  const {template, updateTemplate, setTemplate} = useTemplate()

  const gp = useMemo(() => {
    const raw = getDefaultPaddingStorage(template)
    const block = raw?.block ?? zeroPadding()
    return {
      section: raw?.section ?? zeroPadding(),
      column: raw?.column ?? zeroPadding(),
      block,
      blockMobile: raw?.blockMobile ?? block,
    }
  }, [template])

  const commit = (next: TemplateDefaultPaddingStorage) => {
    const rest = {...template} as Template & {globalPadding?: unknown}
    delete rest.globalPadding
    const updated = {...rest, defaultPadding: next}
    updateTemplate(updated)
    setTemplate(updated)
  }

  const patch = (partial: Partial<TemplateDefaultPaddingStorage>) => {
    const nextSection = partial.section ?? gp.section
    const nextColumn = partial.column ?? gp.column
    const nextBlock = partial.block ?? gp.block
    let nextBlockMobile: Padding
    if (partial.blockMobile !== undefined) {
      nextBlockMobile = partial.blockMobile
    } else if (
      partial.block !== undefined &&
      getDefaultPaddingStorage(template)?.blockMobile === undefined
    ) {
      nextBlockMobile = nextBlock
    } else {
      nextBlockMobile = gp.blockMobile
    }

    commit({
      section: nextSection,
      column: nextColumn,
      block: nextBlock,
      blockMobile: nextBlockMobile,
    })
  }

  const setFieldValue =
    (key: keyof TemplateDefaultPaddingStorage) =>
    (_name: string, value: Padding) => {
      patch({[key]: value})
    }

  if (!editMode) return null

  return (
    <div className="mb-0 pb-1">
      <StyledTitle>{t('templateEditor.defaultPaddingTitle')}</StyledTitle>
      <p
        className="small mb-3"
        style={{
          lineHeight: 1.45,
          color: 'rgba(255, 255, 255, 0.72)',
        }}
      >
        {t('templateEditor.defaultPaddingHint')}
      </p>

      <StyledTitle>{t('templateEditor.defaultPaddingSection')}</StyledTitle>
      <PaddingInput
        value={gp.section}
        name="defaultPadding.section"
        label={t('templateEditor.paddingFieldLabel')}
        setFieldValue={setFieldValue('section')}
        max={500}
        min={0}
      />

      <StyledTitle>{t('templateEditor.defaultPaddingColumn')}</StyledTitle>
      <PaddingInput
        value={gp.column}
        name="defaultPadding.column"
        label={t('templateEditor.paddingFieldLabel')}
        setFieldValue={setFieldValue('column')}
        max={500}
        min={0}
      />

      <StyledTitle>{t('templateEditor.defaultPaddingBlock')}</StyledTitle>
      <PaddingInput
        value={gp.block}
        name="defaultPadding.block"
        label={t('templateEditor.paddingFieldLabel')}
        setFieldValue={setFieldValue('block')}
        max={500}
        min={0}
      />

      <StyledTitle>{t('templateEditor.defaultPaddingBlockMobile')}</StyledTitle>
      <PaddingInput
        value={gp.blockMobile}
        name="defaultPadding.blockMobile"
        label={t('templateEditor.paddingFieldLabel')}
        setFieldValue={setFieldValue('blockMobile')}
        max={500}
        min={0}
      />
    </div>
  )
}
